// Pure, client-safe currency helpers and data. This module must NOT import
// Prisma (or any server-only code) so it can be safely bundled into Client
// Components. Database-backed helpers live in `@/lib/currency`.

export interface CurrencyInfo {
	code: string;
	symbol: string;
	decimalDigits: number;
}

/**
 * Canonical starter currencies. The Bahraini Dinar (BHD) is the platform's
 * base/default currency and, like KWD and OMR, uses three decimal places.
 */
export const CURRENCY_SEED = [
	{
		code: "BHD",
		nameAr: "دينار بحريني",
		nameEn: "Bahraini Dinar",
		symbol: "د.ب",
		decimalDigits: 3,
		rateToBase: 1,
		isDefault: true,
		sortOrder: 0,
	},
	{
		code: "KWD",
		nameAr: "دينار كويتي",
		nameEn: "Kuwaiti Dinar",
		symbol: "د.ك",
		decimalDigits: 3,
		rateToBase: 1.176,
		isDefault: false,
		sortOrder: 1,
	},
	{
		code: "OMR",
		nameAr: "ريال عُماني",
		nameEn: "Omani Rial",
		symbol: "ر.ع",
		decimalDigits: 3,
		rateToBase: 0.98,
		isDefault: false,
		sortOrder: 2,
	},
	{
		code: "SAR",
		nameAr: "ريال سعودي",
		nameEn: "Saudi Riyal",
		symbol: "ر.س",
		decimalDigits: 2,
		rateToBase: 0.1,
		isDefault: false,
		sortOrder: 3,
	},
	{
		code: "AED",
		nameAr: "درهم إماراتي",
		nameEn: "UAE Dirham",
		symbol: "د.إ",
		decimalDigits: 2,
		rateToBase: 0.102,
		isDefault: false,
		sortOrder: 4,
	},
	{
		code: "QAR",
		nameAr: "ريال قطري",
		nameEn: "Qatari Riyal",
		symbol: "ر.ق",
		decimalDigits: 2,
		rateToBase: 0.103,
		isDefault: false,
		sortOrder: 5,
	},
	{
		code: "USD",
		nameAr: "دولار أمريكي",
		nameEn: "US Dollar",
		symbol: "$",
		decimalDigits: 2,
		rateToBase: 0.376,
		isDefault: false,
		sortOrder: 6,
	},
	{
		code: "EUR",
		nameAr: "يورو",
		nameEn: "Euro",
		symbol: "€",
		decimalDigits: 2,
		rateToBase: 0.41,
		isDefault: false,
		sortOrder: 7,
	},
] as const;

export const DEFAULT_CURRENCY_CODE = "BHD";

export const FALLBACK_CURRENCY: CurrencyInfo = {
	code: "BHD",
	symbol: "د.ب",
	decimalDigits: 3,
};

const ARABIC_SCRIPT = /[\u0600-\u06FF]/;

/**
 * The MACHINE-READABLE half of money formatting: a bare decimal string with the
 * currency's decimal digits, dot decimal, no symbol and no thousands
 * separators. That is what a CSV cell, a schema.org `price` and an email
 * `{{total}}` variable want, because each of the three is read by a parser or
 * paired with a separate currency field of its own.
 *
 * `formatMoney` below is the HUMAN half and is built on this one, so the two
 * cannot disagree about how many decimals a currency has. Before TRY-495 the
 * machine half did not exist and fourteen call sites hardcoded three decimals
 * inline, which mailed a 2-decimal store's customer a three-decimal total.
 *
 * Non-finite input formats as zero rather than "NaN": these strings reach a
 * spreadsheet and a search-engine crawler, neither of which has a way to
 * report back. One consequence of per-currency decimals worth knowing: a CSV
 * column of 0-decimal cells no longer sums to a total rounded once at the
 * source, because every cell is rounded to the unit.
 *
 * It is NOT what a reader sees: `moneyDisplay` is, and it groups thousands.
 * Never reach for this one to print an amount on a page.
 */
export function moneyAmount(amount: number, decimalDigits: number): string {
	return (Number.isFinite(amount) ? amount : 0).toFixed(decimalDigits);
}

/**
 * The numbering system every displayed figure in the app resolves through, and
 * the one place that decision is made.
 *
 * Bare `ar` resolves to ICU's `latn`, so both arms emit Latin digits and a
 * comma group separator; the measurement is recorded in
 * `components/dashboard/page-header.tsx`'s docblock. The ternary is therefore
 * inert TODAY and kept deliberately: it is the seam that makes switching to
 * `ar-u-nu-arab` a one-line change here rather than a sweep of every call site.
 */
export function numberingLocale(locale?: string): string {
	return locale === "ar" ? "ar" : "en";
}

/**
 * Cached by `<numbering locale>:<digits>`. `Intl.NumberFormat` construction is
 * the expensive half of formatting, and a table or a chart calls this once per
 * cell.
 */
const displayFormats = new Map<string, Intl.NumberFormat>();

function displayFormat(decimalDigits: number, locale?: string): Intl.NumberFormat {
	const intl = numberingLocale(locale);
	const key = `${intl}:${decimalDigits}`;
	let f = displayFormats.get(key);
	if (!f) {
		f = new Intl.NumberFormat(intl, {
			minimumFractionDigits: decimalDigits,
			maximumFractionDigits: decimalDigits,
		});
		displayFormats.set(key, f);
	}
	return f;
}

/**
 * The HUMAN half of an amount: the currency's decimal digits AND thousands
 * groups, so a figure a person reads is `1,176.024` and never `1176.024`
 * (TRY-503 put the decimals in one place; grouping was still per-call-site, and
 * only the admin KPI tiles asked for it).
 *
 * Grouped is the DEFAULT, not an option. A displayed amount with no groups is
 * the defect, so there is deliberately no ungrouped display variant to reach
 * for: anything that must stay ungrouped is machine-read and wants
 * `moneyAmount` — a CSV cell, a schema.org `price`, an email `{{total}}`, or
 * the `defaultValue` of a money `<input type="number">`, whose own parser
 * rejects a separator.
 *
 * Non-finite input formats as zero, matching `moneyAmount`, so the two halves
 * cannot disagree about a bad number either.
 */
export function moneyDisplay(amount: number, decimalDigits: number, locale?: string): string {
	return displayFormat(decimalDigits, locale).format(Number.isFinite(amount) ? amount : 0);
}

/**
 * Format an amount for a READER: grouped digits plus the currency's symbol.
 * When a `locale` is provided, the symbol is localized (e.g. Gulf currencies
 * show their ISO code like "BHD" in non-Arabic locales instead of "د.ب").
 *
 * An absent `locale` keeps the stored symbol and still groups, because
 * `numberingLocale` puts both of its arms on the same separator; a client
 * component that formats without a locale therefore renders what the server
 * rendered, and hydration matches.
 */
export function formatMoney(amount: number, info: CurrencyInfo, locale?: string): string {
	const symbol = locale ? localizedSymbol(info.symbol, info.code, locale) : info.symbol;
	return `${moneyDisplay(amount, info.decimalDigits, locale)} ${symbol}`;
}

/**
 * Choose the symbol that reads well in the active locale. Gulf currencies are
 * stored with their Arabic symbol (e.g. "د.ب"); in non-Arabic locales we show
 * the ISO code instead (e.g. "BHD"). Latin symbols like "$"/"€" are kept as-is.
 */
export function localizedSymbol(symbol: string, code: string, locale: string): string {
	if (locale !== "ar" && ARABIC_SCRIPT.test(symbol)) return code;
	return symbol;
}

/**
 * The `step` a money `<input type="number">` should carry for a currency: one
 * minor unit, i.e. `10 ** -decimalDigits`. This is the one shared expression of
 * it. Before TRY-488 the derivation was restated inline at 23 call sites, 19 of
 * them as a hardcoded three-decimal literal that invited a third decimal on a
 * 2-decimal store and was meaningless on a 0-decimal one. That literal is
 * deliberately not spelled out here: TRY-488's acceptance criteria are a grep
 * for it returning zero hits, and a docblock quoting it is a hit.
 *
 * `decimalDigits` is bounded 0..4 by `validation/currency.ts`, so the result is
 * always a plain decimal in attribute form: 0 gives "1", 2 gives "0.01", 4
 * gives "0.0001". No exponential notation can reach the DOM.
 */
export function moneyStep(decimalDigits: number): number {
	return 10 ** -decimalDigits;
}

// ---------------------------------------------------------------------------
// Exchange rates / conversion (pure)
// ---------------------------------------------------------------------------

export interface RateEntry extends CurrencyInfo {
	rateToBase: number; // value of 1 unit expressed in the platform base (BHD)
}
export type RateTable = Map<string, RateEntry>;

/**
 * Convert an amount between two currencies using their rate-to-base.
 * amount_to = amount_from * (rateToBase_from / rateToBase_to)
 * Falls back to the original amount if a rate is missing.
 */
export function convert(amount: number, from: string, to: string, table: RateTable): number {
	if (from === to) return amount;
	const f = table.get(from);
	const t = table.get(to);
	if (!f || !t || t.rateToBase === 0) return amount;
	return amount * (f.rateToBase / t.rateToBase);
}
